import {
  PackageSize,
  ShipmentStopType,
  ShipmentType,
} from "@api/graphql/generated/generated-types";
import { shouldSortByEntryOrder } from "@src/shipments/features/stopReordering";
import moment from "moment";
import { momentToTimePickerDate } from "@src/common/utils/timezoneConverters";
import { formatPhoneNumber } from "@src/common/lib/textUtils";
import { hydrateBasePackageFromShipment } from "@src/common/utils/packageItems";

interface InitializeFormValuesStop {
  id: string;
  type: ShipmentStopType;
  stopDate?: string | null;
  stopSequenceNumber?: number | null;
  orderEntrySequenceNumber?: number | null;
  stopConsolidatedSortingNumber?: number | null;
  streetAddress?: string | null;
  city?: string | null;
  state?: string | null;
  country?: string | null;
  zipCode?: string | null;
  companyName?: string | null;
  contactName?: string | null;
  googlePlaceId?: string | null;
  instruction?: string | null;
  phone?: string | null;
  suite?: string | null;
  locationId?: string | null;
  additionalDriverQuestionListId?: string | null;
  timeWindow?: {
    open?: string | null;
    close?: string | null;
  } | null;
  tasks?: Array<{ sequenceNumber: number }> | null;
}

interface InitializeFormValuesShipment {
  shipmentDate: string;
  packageSize?: PackageSize | null;
  numPackages?: number | null;
  type: ShipmentType;
  packageWeightInPounds?: number | null;
  packageDescription?: string | null;
  packageType?: { id: string } | null;
  vehicleType?: { id: string } | null;
  packageItems?: Array<{
    width?: number | null;
    height?: number | null;
    length?: number | null;
    weight?: number | null;
    packageTypeId?: string | null;
    quantity?: number | null;
  }> | null;
  stops: InitializeFormValuesStop[];
  order: {
    id: string;
    courierId: string;
    caller?: string | null;
    callerPhone?: string | null;
    endCustomerReferenceNumber?: string | null;
    additionalEmailRecipients?: string[] | null;
    serviceType?: { id: string; name: string } | null;
    endCustomer: { id: string };
    customFieldValues: Array<{
      valueText?: string | null;
      valueInteger?: number | null;
      valueNumber?: number | null;
      field: { id: string };
    }>;
  };
}

export function initializeFormValues(
  shipmentSelectedForAction: InitializeFormValuesShipment,
  shouldKeepOrderEntrySequence: boolean = false
) {
  const hydratedPackageItems = hydrateBasePackageFromShipment({
    packageItems: shipmentSelectedForAction.packageItems,
    numPackages: shipmentSelectedForAction.numPackages,
    packageWeightInPounds: shipmentSelectedForAction.packageWeightInPounds,
    packageTypeId: shipmentSelectedForAction.packageType?.id,
  });

  const customFieldValues =
    shipmentSelectedForAction.order.customFieldValues.reduce(
      (acc, customField) => {
        acc[customField.field.id] = customField.valueText
          ? customField.valueText
          : customField.valueInteger ?? customField.valueNumber ?? undefined;
        return acc;
      },
      {} as { [key: string]: string | number | undefined }
    );

  // Mirror the order-detail widget (makeStopPanelProps): select ONE sort
  // mode for the whole shipment rather than a per-stop fallback. If any stop
  // has a task sequence, sort every stop by task sequence (stops without one
  // go last); otherwise sort every stop by consolidated sorting number.
  // Mixing the two key-spaces per stop diverges from the widget for
  // mixed-dispatch shipments (some stops dispatched, some not).
  const hasAnyTaskSequence = shipmentSelectedForAction.stops.some(
    (stop) => stop.tasks?.[0]?.sequenceNumber != null
  );

  // "Keep the Order Entry Sequence" couriers: the entry order stays put after
  // creation, even when a driver or dispatcher re-sequences the tasks.
  const hasAnyEntrySequence: boolean = shipmentSelectedForAction.stops.some(
    (stop) => stop.stopConsolidatedSortingNumber != null
  );
  const shouldSortByTaskSequence: boolean =
    hasAnyTaskSequence &&
    !(shouldKeepOrderEntrySequence && hasAnyEntrySequence);

  const shouldSortByEntryNumber: boolean = shouldSortByEntryOrder({
    stops: shipmentSelectedForAction.stops,
    shouldKeepOrderEntrySequence,
  });

  const sortKey = (stop: InitializeFormValuesStop) => {
    if (shouldSortByTaskSequence) {
      return stop.tasks?.[0]?.sequenceNumber ?? Number.MAX_SAFE_INTEGER;
    }
    if (shouldSortByEntryNumber) {
      return stop.orderEntrySequenceNumber ?? Number.MAX_SAFE_INTEGER;
    }
    return stop.stopConsolidatedSortingNumber ?? Number.MAX_SAFE_INTEGER;
  };

  return {
    id: shipmentSelectedForAction.order.id,
    endCustomerReferenceNumber:
      shipmentSelectedForAction.order.endCustomerReferenceNumber,
    // shipment dates are stored as JavaScript UTC date w/o TimeZone information
    // so we just replace - with / to make it local TimeZone
    shipmentDate: new Date(
      shipmentSelectedForAction.shipmentDate.replaceAll("-", "/")
    ),
    courierId: shipmentSelectedForAction.order.courierId,
    caller: shipmentSelectedForAction.order.caller,
    callerPhone: shipmentSelectedForAction.order.callerPhone,
    endCustomerId: shipmentSelectedForAction.order.endCustomer.id,
    packageSize: shipmentSelectedForAction.packageSize,
    numPackages: shipmentSelectedForAction.numPackages,
    type: shipmentSelectedForAction.type,
    packageWeightInPounds: shipmentSelectedForAction.packageWeightInPounds,
    serviceType: shipmentSelectedForAction.order.serviceType,
    stops: [...shipmentSelectedForAction.stops]
      .sort((a, b) => {
        const taskSequenceDifference = sortKey(a) - sortKey(b);
        if (a.type === ShipmentStopType.Return) return 1;
        if (b.type === ShipmentStopType.Return) return -1;
        if (taskSequenceDifference !== 0) return taskSequenceDifference;
        if (
          a.type === ShipmentStopType.PickUp &&
          b.type !== ShipmentStopType.PickUp
        )
          return -1;
        if (
          a.type !== ShipmentStopType.PickUp &&
          b.type === ShipmentStopType.PickUp
        )
          return 1;
        return 0;
      })
      .map((stop) => ({
        streetAddress: [stop.streetAddress, stop.city, stop.state, [stop.country, stop.zipCode].filter(Boolean).join(" ")].filter(Boolean).join(", "),
        companyName: stop.companyName,
        contactName: stop.contactName,
        googlePlaceId: stop.googlePlaceId,
        id: stop.id,
        instruction: stop.instruction,
        phone: stop.phone && formatPhoneNumber(stop.phone),
        suite: stop.suite,
        timeWindow: stop.timeWindow
          ? {
              open: stop.timeWindow.open,
              close: stop.timeWindow.close,
            }
          : undefined,
        type: stop.type,
        stopDate: stop.stopDate
          ? // stop dates are stored as JavaScript UTC date w/o TimeZone information
            // so we just replace - with / to make it local TimeZone
            new Date(stop.stopDate.replaceAll("-", "/"))
          : undefined,
        // For non-pickup stops, set deliverByDate from timeWindow.close
        deliverByDate:
          stop.timeWindow?.close && stop.type !== ShipmentStopType.PickUp
            ? momentToTimePickerDate(
                moment(Number(stop.timeWindow.close) * 1000)
              )
            : undefined,
        // For pickup stops, set pickupByDate from timeWindow.close
        pickupByDate:
          stop.timeWindow?.close && stop.type === ShipmentStopType.PickUp
            ? momentToTimePickerDate(
                moment(Number(stop.timeWindow.close) * 1000)
              )
            : undefined,
        locationId: stop.locationId,
        additionalDriverQuestionListId: stop.additionalDriverQuestionListId,
      })),
    packageTypeId: shipmentSelectedForAction.packageType?.id || null,
    vehicleTypeId: shipmentSelectedForAction.vehicleType?.id || null,
    packageDescription: shipmentSelectedForAction.packageDescription,
    packageItems: hydratedPackageItems.map((item) => ({
        width: item.width ?? null,
        height: item.height ?? null,
        length: item.length ?? null,
        weight: item.weight ?? null,
        packageTypeId: item.packageTypeId ?? null,
        quantity: item.quantity ?? null,
      })),
    customFieldValues,
    additionalEmailRecipients:
      shipmentSelectedForAction.order.additionalEmailRecipients?.join(",") ||
      undefined,
  };
}
